import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FileUp, 
  X, 
  Send, 
  Shield, 
  Users, 
  Heart, 
  CheckCircle,
  Globe,
  MapPin,
  Plane,
  Home as HomeIcon,
  Building
} from 'lucide-react';
import StudyRequestForm from '../components/Site/StudyRequestForm';  // eslint-disable-line no-unused-vars -- composant conservé (non branché actuellement)
import DynamicFormModal from '../components/Site/DynamicFormModal';
import logger from '../utils/logger';

const Home = () => {
  const { t } = useTranslation('public');
  const [selectedFormSlug, setSelectedFormSlug] = useState(null);
  const [dynamicPartners, setDynamicPartners] = useState([]);
  const [heroSettings, setHeroSettings] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroVideoError, setHeroVideoError] = useState(false);
  // Image de secours (poster + fallback) affichée si la vidéo hero ne se charge pas (ex: .mov non lu sur certains Mac/navigateurs)
  const HERO_FALLBACK_IMG = 'https://images.unsplash.com/photo-1758599543130-5fc443dea135?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // Charger les paramètres du hero
  useEffect(() => {
    const fetchHeroSettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/site-settings/hero`);
        if (response.ok) {
          const data = await response.json();
          setHeroSettings(data);
          setHeroVideoError(false);
        }
      } catch (error) {
        logger.error('Erreur lors du chargement des paramètres hero:', error);
      }
    };
    fetchHeroSettings();
  }, [API_BASE_URL]);

  // Carrousel auto-play
  useEffect(() => {
    if (heroSettings?.mode === 'carousel' && heroSettings?.autoplay && heroSettings?.slides?.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % heroSettings.slides.length);
      }, heroSettings.interval || 5000);
      return () => clearInterval(interval);
    }
  }, [heroSettings]);

  // Charger les compagnies partenaires depuis l'API
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/public/partners`);
        if (response.ok) {
          const companies = await response.json();
          if (companies && companies.length > 0) {
            // Transformer les données pour le format du carrousel
            const formattedPartners = companies.map(company => ({
              name: company.name,
              logo: company.logo_url ? `${API_BASE_URL}${company.logo_url}` : null,
              color: 'text-blue-600'
            }));
            setDynamicPartners(formattedPartners);
          }
        }
      } catch (error) {
        logger.error('Erreur lors du chargement des partenaires:', error);
      }
    };

    fetchPartners();
  }, [API_BASE_URL]);

  // Partenaires par défaut (fallback)
  const defaultPartners = [
    { name: 'AXA', logo: 'https://logos-world.net/wp-content/uploads/2020/11/AXA-Logo.png', color: 'text-blue-600' },
    { name: 'APRIL', logo: 'https://www.april.com/sites/default/files/2021-11/logo-april-group.png', color: 'text-red-500' },
    { name: 'Henner', logo: 'https://www.henner.com/wp-content/themes/henner-child/assets/images/logo-henner.svg', color: 'text-blue-700' },
    { name: 'Humanis', logo: 'https://www.humanis.com/sites/default/files/2021-04/logo-humanis.png', color: 'text-green-600' },
    { name: 'My Veralti', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzM0QjBEOCIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiPk15IFZlcmFsdGk8L3RleHQ+PC9zdmc+', color: 'text-blue-500' },
    { name: 'Entoria', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzE2QTM0QSIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiPkVudG9yaWE8L3RleHQ+PC9zdmc+', color: 'text-green-600' },
    { name: 'Helvetia', logo: 'https://www.helvetia.com/content/dam/os/corporate/web/images/logo/helvetia-logo.svg', color: 'text-red-600' },
    { name: 'Mutuelle Mieux Être', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzM0QjBEOCIgZm9udC1zaXplPSIxMiIgZm9udC13ZWlnaHQ9ImJvbGQiPk11dHVlbGxlPC90ZXh0Pjx0ZXh0IHg9IjUwIiB5PSIzMiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzM0QjBEOCIgZm9udC1zaXplPSIxMiIgZm9udC13ZWlnaHQ9ImJvbGQiPk1pZXV4IMOKdHJlPC90ZXh0Pjwvc3ZnPg==', color: 'text-blue-500' },
    { name: 'ACS', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiJ2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzk5MzMzMyIgZm9udC1zaXplPSIxOCIgZm9udC13ZWlnaHQ9ImJvbGQiPkFDUzwvdGV4dD48L3N2Zz4=', color: 'text-red-700' },
    { name: 'Matrisk', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzE2QTM0QSIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiPk1hdHJpc2s8L3RleHQ+PC9zdmc+', color: 'text-green-600' },
    { name: 'Solly Azar', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzM0QjBEOCIgZm9udC1zaXplPSIxMiIgZm9udC13ZWlnaHQ9ImJvbGQiPlNvbGx5IEF6YXI8L3RleHQ+PC9zdmc+', color: 'text-blue-600' },
    { name: 'Tetris', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiJ2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzk5MzMzMyIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiPlRldHJpczwvdGV4dD48L3N2Zz4=', color: 'text-red-600' },
    { name: 'Zenioo', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzE2QTM0QSIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiPlplbmlvbzwvdGV4dD48L3N2Zz4=', color: 'text-green-500' },
    { name: 'Zephir', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzM0QjBEOCIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiPlplcGhpcjwvdGV4dD48L3N2Zz4=', color: 'text-blue-500' },
    { name: 'ECA', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzk5MzMzMyIgZm9udC1zaXplPSIxOCIgZm9udC13ZWlnaHQ9ImJvbGQiPkVDQTwvdGV4dD48L3N2Zz4=', color: 'text-red-700' },
    { name: 'Assurmax', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzE2QTM0QSIgZm9udC1zaXplPSIxMiIgZm9udC13ZWlnaHQ9ImJvbGQiPkFzc3VybWF4PC90ZXh0Pjwvc3ZnPg==', color: 'text-green-600' },
    { name: '+Simple', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzM0QjBEOCIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiPitTaW1wbGU8L3RleHQ+PC9zdmc+', color: 'text-blue-500' },
    { name: 'Corim', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzk5MzMzMyIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiPkNvcmltPC90ZXh0Pjwvc3ZnPg==', color: 'text-red-600' },
    { name: 'Assuréa', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzE2QTM0QSIgZm9udC1zaXplPSIxMiIgZm9udC13ZWlnaHQ9ImJvbGQiPkFzc3Vyw6lhPC90ZXh0Pjwvc3ZnPg==', color: 'text-green-500' },
    { name: 'Kereis', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzM0QjBEOCIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiPktlcmVpczwvdGV4dD48L3N2Zz4=', color: 'text-blue-600' },
    { name: 'Spvie', logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjUwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzk5MzMzMyIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiPlNwdmllPC90ZXh0Pjwvc3ZnPg==', color: 'text-red-700' }
  ];

  // Utiliser les partenaires dynamiques s'ils existent, sinon les partenaires par défaut
  const partners = dynamicPartners.length > 0 ? dynamicPartners : defaultPartners;

  // Solutions d'assurance avec traductions
  const services = [
    {
      id: 'particuliers',
      title: t('home.solutions.individuals.title'),
      icon: <HomeIcon size={48} />,
      description: t('home.solutions.individuals.description'),
      items: [t('solutions.individuals.auto.title'), t('solutions.individuals.home.title'), t('solutions.individuals.health.title'), t('solutions.individuals.life.title')],
      color: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600'
    },
    {
      id: 'professionnels',
      title: t('home.solutions.professionals.title'),
      icon: <Building size={48} />,
      description: t('home.solutions.professionals.description'),
      items: [t('solutions.professionals.rcPro.title'), t('solutions.professionals.multirisque.title'), t('solutions.professionals.fleet.title'), t('solutions.professionals.cyber.title')],
      color: 'bg-green-50 border-green-200',
      iconColor: 'text-green-600'
    },
    {
      id: 'expatries',
      title: t('home.solutions.expatriates.title'),
      icon: <Globe size={48} />,
      description: t('home.solutions.expatriates.description'),
      items: [t('solutions.expatriates.internationalHealth.title'), t('solutions.expatriates.assistance.title'), t('solutions.expatriates.familyPack.title')],
      color: 'bg-purple-50 border-purple-200',
      iconColor: 'text-purple-600'
    },
    {
      id: 'voyage',
      title: t('home.solutions.travel.title'),
      icon: <Plane size={48} />,
      description: t('home.solutions.travel.description'),
      items: [t('solutions.travel.cancellation.title'), t('solutions.travel.luggage.title'), t('solutions.travel.assistanceTravel.title')],
      color: 'bg-orange-50 border-orange-200',
      iconColor: 'text-orange-600'
    }
  ];

  const whyChooseUs = [
    {
      icon: <CheckCircle size={40} className="text-brand-primary" />,
      title: t('home.whyUs.expertise.title'),
      description: t('home.whyUs.expertise.description')
    },
    {
      icon: <Users size={40} className="text-brand-primary" />,
      title: t('home.whyUs.proximity.title'),
      description: t('home.whyUs.proximity.description')
    },
    {
      icon: <Heart size={40} className="text-brand-primary" />,
      title: t('home.whyUs.trust.title'),
      description: t('home.whyUs.trust.description')
    },
    {
      icon: <Shield size={40} className="text-brand-primary" />,
      title: t('common.seeMore'),
      description: t('home.solutions.subtitle')
    }
  ];

  const documentTypes = {
    auto: ['Permis de conduire', 'Carte grise', 'Relevé de sinistralité'],
    habitation: ['Justificatif d\'identité', 'Relevé d\'information'],
    professionnel: ['Kbis', 'Attestation décennale', 'RIB'],
    expat: ['Passeport', 'Attestations sociales/fiscales'],
    general: ['RIB', 'Justificatif d\'identité']
  };

  // Fonctions obsolètes supprimées - carrousel automatique

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Présentation du cabinet */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="mb-6 md:mb-8">
                <div className="bg-gradient-to-r from-brand-primary to-brand-primary-hover text-white px-4 py-2 md:px-6 md:py-3 rounded-full text-sm md:text-lg font-bold inline-flex items-center shadow-lg">
                  <CheckCircle className="mr-2 md:mr-3" size={20} />
                  <span className="text-xs md:text-base">{t('home.hero.badge')}</span>
                </div>
              </div>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-brand-text mb-4 md:mb-6 leading-tight">
                {t('home.hero.title')}
              </h1>
              <h2 className="text-xl md:text-2xl text-brand-primary font-semibold mb-4 md:mb-6">
                {t('home.hero.subtitle')}
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed">
                {t('home.hero.description')}
              </p>
              <div className="flex flex-col gap-4">
                <button
                  onClick={() => setSelectedFormSlug('demande-generale')}
                  className="w-full group bg-gradient-to-r from-brand-primary to-brand-primary-hover text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold text-base md:text-lg hover:from-brand-primary-hover hover:to-brand-primary-dark transition-all duration-500 hover:shadow-2xl hover:scale-105 md:hover:scale-110 transform hover:-translate-y-1 md:hover:-translate-y-2 relative overflow-hidden hover:animate-none"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    <span className="mr-2">{t('common.requestStudy')}</span>
                    <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">✨</span>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-brand-primary to-brand-primary-hover rounded-lg blur opacity-0 group-hover:opacity-75 transition-opacity duration-300 -z-10"></div>
                </button>
                <Link
                  to="/contact"
                  className="w-full group border-2 border-brand-primary text-brand-primary px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold text-base md:text-lg hover:bg-brand-primary hover:text-white transition-all duration-500 text-center hover:shadow-2xl hover:scale-105 md:hover:scale-110 transform hover:-translate-y-1 md:hover:-translate-y-2 relative overflow-hidden hover:border-brand-primary-hover"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    <span className="mr-2">{t('common.contactUs')}</span>
                    <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-brand-primary to-brand-primary-hover transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-brand-primary to-brand-primary-hover rounded-lg blur opacity-0 group-hover:opacity-50 transition-opacity duration-300 -z-10"></div>
                </Link>
              </div>
            </div>
            <div className="flex justify-center order-1 lg:order-2 mb-6 lg:mb-0">
              <div className="rounded-2xl shadow-2xl relative w-72 h-72 md:w-96 md:h-96 lg:w-[500px] lg:h-[500px] overflow-hidden">
                {/* Hero Media - Dynamique */}
                {heroSettings?.slides?.length > 0 ? (
                  <>
                    {heroSettings.mode === 'carousel' ? (
                      // Mode Carrousel
                      <div className="relative w-full h-full">
                        {heroSettings.slides.map((slide, index) => (
                          <div
                            key={slide.id}
                            className={`absolute inset-0 transition-opacity duration-700 ${
                              index === currentSlide ? 'opacity-100' : 'opacity-0'
                            }`}
                          >
                            {slide.type === 'video' ? (
                              <video
                                src={slide.url}
                                autoPlay
                                loop
                                muted
                                playsInline
                                poster={HERO_FALLBACK_IMG}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <img
                                src={slide.url}
                                alt={`Slide ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            )}
                            {slide.overlay_text && (
                              <div 
                                className={`absolute inset-x-0 px-4 py-3 text-center ${
                                  slide.overlay_position === 'top' ? 'top-0' :
                                  slide.overlay_position === 'bottom' ? 'bottom-0' : 'top-1/2 -translate-y-1/2'
                                } ${slide.text_size === 'sm' ? 'text-sm' : 
                                    slide.text_size === 'lg' ? 'text-lg' : 
                                    slide.text_size === 'xl' ? 'text-xl' : 
                                    slide.text_size === '2xl' ? 'text-2xl' : 'text-base'
                                } ${slide.text_bold ? 'font-bold' : ''} ${slide.text_italic ? 'italic' : ''}`}
                                style={{
                                  color: slide.text_color || '#ffffff',
                                  backgroundColor: slide.text_bg_color || 'rgba(0,0,0,0.6)'
                                }}
                              >
                                {slide.overlay_text}
                              </div>
                            )}
                          </div>
                        ))}
                        {/* Indicateurs du carrousel */}
                        {heroSettings.slides.length > 1 && (
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                            {heroSettings.slides.map((_, index) => (
                              <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`w-2 h-2 rounded-full transition-all ${
                                  index === currentSlide ? 'bg-white w-4' : 'bg-white/50'
                                }`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      // Mode Single
                      <>
                        {heroSettings.slides[0]?.type === 'video' && !heroVideoError ? (
                          <video
                            src={heroSettings.slides[0]?.url}
                            autoPlay
                            loop
                            muted
                            playsInline
                            poster={HERO_FALLBACK_IMG}
                            className="w-full h-full object-cover"
                            onError={() => setHeroVideoError(true)}
                          />
                        ) : (
                          <img
                            src={heroSettings.slides[0]?.type === 'video' ? HERO_FALLBACK_IMG : heroSettings.slides[0]?.url}
                            alt="Hero"
                            className="w-full h-full object-cover"
                          />
                        )}
                        {heroSettings.slides[0]?.overlay_text && (
                          <div 
                            className={`absolute inset-x-0 px-4 py-3 text-center ${
                              heroSettings.slides[0]?.overlay_position === 'top' ? 'top-0' :
                              heroSettings.slides[0]?.overlay_position === 'bottom' ? 'bottom-0' : 'top-1/2 -translate-y-1/2'
                            } ${heroSettings.slides[0]?.text_size === 'sm' ? 'text-sm' : 
                                heroSettings.slides[0]?.text_size === 'lg' ? 'text-lg' : 
                                heroSettings.slides[0]?.text_size === 'xl' ? 'text-xl' : 
                                heroSettings.slides[0]?.text_size === '2xl' ? 'text-2xl' : 'text-base'
                            } ${heroSettings.slides[0]?.text_bold ? 'font-bold' : ''} ${heroSettings.slides[0]?.text_italic ? 'italic' : ''}`}
                            style={{
                              color: heroSettings.slides[0]?.text_color || '#ffffff',
                              backgroundColor: heroSettings.slides[0]?.text_bg_color || 'rgba(0,0,0,0.6)'
                            }}
                          >
                            {heroSettings.slides[0]?.overlay_text}
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  // Fallback si aucun média configuré : image de secours (pas de .mov non fiable)
                  <img
                    src={HERO_FALLBACK_IMG}
                    alt="Hero"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-text mb-3 sm:mb-4">
              {t('home.solutions.title')}
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              {t('home.solutions.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`/solutions/${service.id}`}
                className={`${service.color} border-2 rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all duration-300 active:scale-[0.98] sm:hover:scale-105 cursor-pointer`}
              >
                <div className={`${service.iconColor} mb-3 sm:mb-4`}>
                  {React.cloneElement(service.icon, { size: 36, className: 'sm:w-12 sm:h-12' })}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-brand-text mb-2 sm:mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-3 sm:mb-4 text-sm">
                  {service.description}
                </p>
                <ul className="space-y-1.5 sm:space-y-2">
                  {service.items.map((item, index) => (
                    <li key={index} className="flex items-center text-xs sm:text-sm text-gray-700">
                      <CheckCircle size={14} className="text-brand-primary mr-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="mt-3 sm:mt-4 text-brand-primary font-semibold text-sm hover:underline">
                  {t('common.seeMore')} →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-text mb-4">
              {t('home.whyUs.title')}
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-3 sm:mb-6">
                  <div className="bg-blue-50 p-3 sm:p-4 rounded-full">
                    {React.cloneElement(item.icon, { size: 28, className: 'sm:w-10 sm:h-10' })}
                  </div>
                </div>
                <h3 className="text-sm sm:text-lg lg:text-xl font-bold text-brand-text mb-2 sm:mb-4">
                  {item.title}
                </h3>
                <p className="text-xs sm:text-sm lg:text-base text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Carousel - Défilement continu */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-brand-text mb-3 sm:mb-4">
              {t('home.partners.title')}
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              {t('home.partners.subtitle')}
            </p>
          </div>

          <div className="relative">
            {/* Carrousel défilant infini */}
            <div className="flex overflow-hidden">
              <div className="flex animate-scroll space-x-4 sm:space-x-8 items-center">
                {[...partners, ...partners, ...partners].map((partner, index) => (
                  <div
                    key={index}
                    className="bg-white p-3 sm:p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 sm:hover:scale-110 transform cursor-pointer flex-shrink-0 flex items-center justify-center"
                    style={{ minWidth: '120px', minHeight: '80px' }}
                  >
                    {partner.logo ? (
                      <>
                        <img 
                          src={partner.logo} 
                          alt={partner.name}
                          className="h-8 sm:h-12 w-auto object-contain mx-auto transition-all duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) {
                              e.target.nextSibling.style.display = 'block';
                            }
                          }}
                        />
                        <div className="hidden text-center">
                          <span className={`text-sm sm:text-lg font-semibold ${partner.color}`}>
                            {partner.name}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="text-center">
                        <span className={`text-sm sm:text-lg font-semibold ${partner.color}`}>
                          {partner.name}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-r from-brand-primary to-brand-primary-hover">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6">
            {t('home.cta.title')}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-6 sm:mb-8">
            {t('home.cta.subtitle')}
          </p>
          <button
            onClick={() => setSelectedFormSlug('demande-generale')}
            className="group bg-white text-brand-primary px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-gray-50 transition-all duration-500 shadow-lg hover:shadow-2xl active:scale-95 sm:hover:scale-110 transform sm:hover:-translate-y-2 relative overflow-hidden"
          >
            <span className="relative z-10 flex items-center justify-center">
              <span className="mr-2">{t('home.cta.button')}</span>
              <span className="inline-block transition-transform duration-300 group-hover:rotate-12 group-hover:scale-125">🚀</span>
            </span>
          </button>
        </div>
      </section>

      {selectedFormSlug && (
        <DynamicFormModal
          slug={selectedFormSlug}
          onClose={() => setSelectedFormSlug(null)}
          backendUrl={process.env.REACT_APP_BACKEND_URL}
        />
      )}
    </div>
  );
};

export default Home;